import React, { useState } from 'react';
import { useCookies } from '../contexts/CookieContext';
import { useTheme } from '../contexts/ThemeContext';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

export default function CookieBanner() {
  const { theme } = useTheme();
  const { showConsentBanner, acceptAllCookies, rejectAllCookies, updateCookieConsent } = useCookies();
  const [showDetails, setShowDetails] = useState(false);
  const [customConsent, setCustomConsent] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });

  if (!showConsentBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      <div className={`
        p-4 md:p-6 shadow-lg border-t
        ${theme === 'dark'
          ? 'bg-gray-900 border-gray-800'
          : 'bg-white border-gray-200'
        }
      `}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <Cookie className="h-6 w-6 text-blue-500 mt-1" />
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  Cookie Preferences
                </h3>
                <p className={`text-sm mb-4 max-w-2xl ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  By clicking "Accept All", you consent to our use of cookies.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDetails(prev => !prev)}
              className={`
                p-2 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'hover:bg-gray-800'
                  : 'hover:bg-gray-100'
                }
              `}
            >
              {showDetails ? (
                <ChevronDown className="h-5 w-5" />
              ) : (
                <ChevronUp className="h-5 w-5" />
              )}
            </button>
          </div>

          {showDetails && (
            <div className="mt-4 space-y-4">
              {[
                {
                  id: 'necessary',
                  label: 'Necessary',
                  description: 'Essential cookies that enable basic website functionality.',
                  required: true
                },
                {
                  id: 'analytics',
                  label: 'Analytics',
                  description: 'Cookies that help us understand how you interact with our website.'
                },
                {
                  id: 'marketing',
                  label: 'Marketing',
                  description: 'Cookies used to deliver relevant advertisements and track their effectiveness.'
                },
                {
                  id: 'preferences',
                  label: 'Preferences',
                  description: 'Cookies that remember your settings and preferences.'
                }
              ].map(cookie => (
                <div key={cookie.id} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id={cookie.id}
                    checked={cookie.required || customConsent[cookie.id as keyof typeof customConsent]}
                    onChange={(e) => {
                      if (!cookie.required) {
                        setCustomConsent(prev => ({
                          ...prev,
                          [cookie.id]: e.target.checked
                        }));
                      }
                    }}
                    disabled={cookie.required}
                    className="mt-1"
                  />
                  <div>
                    <label
                      htmlFor={cookie.id}
                      className={`block font-medium ${
                        theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                      }`}
                    >
                      {cookie.label}
                      {cookie.required && (
                        <span className="ml-2 text-xs text-gray-500">(Required)</span>
                      )}
                    </label>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {cookie.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-4 mt-6">
            <button
              onClick={acceptAllCookies}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Accept All
            </button>
            <button
              onClick={rejectAllCookies}
              className={`
                px-4 py-2 rounded-lg transition-colors
                ${theme === 'dark'
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              Reject All
            </button>
            {showDetails && (
              <button
                onClick={() => updateCookieConsent(customConsent)}
                className={`
                  px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Save Preferences
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}